import React from 'react'
import { Box, Button, Flex } from '@workwhile/ui'
import { ColumnDef } from '@tanstack/react-table'
import { HighnoteWebhookEventItem } from 'api/highnote/notificationTasks'
import { TimeConversionPopover } from 'components/Shared/TimeConversion'
import { getLocalTimezone } from 'lib/time'
import { useNotificationTasksContextValue } from '../../../context/NotificationTasksProvider'

const TimestampCell = ({ value }: { value: string }) => (
  <TimeConversionPopover
    value={value}
    zones={[
      { timeZone: 'UTC', context: 'System' },
      { timeZone: getLocalTimezone(), context: 'Your computer' },
    ]}
    primaryTimeZone={getLocalTimezone()}
  />
)

const PayloadCell = ({ payload }: { payload: string }) => {
  const {
    actions: { setModalPayload },
  } = useNotificationTasksContextValue()

  return (
    <Box style={{ position: 'relative', maxWidth: 360, height: 80 }}>
      <Box
        style={{
          filter: 'blur(4px)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'monospace',
          fontSize: 12,
          userSelect: 'none',
          maxHeight: 80,
          overflow: 'hidden',
        }}
      >
        {payload}
      </Box>
      <Flex
        style={{
          position: 'absolute',
          inset: 0,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Button variant="secondary" onClick={() => setModalPayload(payload)}>
          Reveal
        </Button>
      </Flex>
    </Box>
  )
}

export const selectableColumns = [
  {
    id: 'status',
    label: 'Status',
    columnDef: {
      header: 'Status',
      accessorKey: 'status',
    } satisfies ColumnDef<HighnoteWebhookEventItem>,
  },
  {
    id: 'eventName',
    label: 'Event Name',
    columnDef: {
      header: 'Event Name',
      accessorKey: 'eventName',
      cell: ({ row }) => row.original.eventName ?? '—',
    } satisfies ColumnDef<HighnoteWebhookEventItem>,
  },
  {
    id: 'eventId',
    label: 'Event ID',
    columnDef: {
      header: 'Event ID',
      accessorKey: 'eventId',
      cell: ({ row }) => row.original.eventId ?? '—',
    } satisfies ColumnDef<HighnoteWebhookEventItem>,
  },
  {
    id: 'replay',
    label: 'Replay',
    columnDef: {
      header: 'Replay',
      accessorKey: 'replay',
      cell: ({ row }) => (row.original.replay ? 'Yes' : 'No'),
    } satisfies ColumnDef<HighnoteWebhookEventItem>,
  },
  {
    id: 'createdAt',
    label: 'Created',
    columnDef: {
      header: 'Created',
      accessorKey: 'createdAt',
      minSize: 180,
      cell: ({ row }) => <TimestampCell value={row.original.createdAt} />,
    } satisfies ColumnDef<HighnoteWebhookEventItem>,
  },
  {
    id: 'updatedAt',
    label: 'Updated',
    columnDef: {
      header: 'Updated',
      accessorKey: 'updatedAt',
      minSize: 180,
      cell: ({ row }) => <TimestampCell value={row.original.updatedAt} />,
    } satisfies ColumnDef<HighnoteWebhookEventItem>,
  },
  {
    id: 'payload',
    label: 'Payload',
    columnDef: {
      header: 'Payload',
      accessorKey: 'payload',
      enableSorting: false,
      cell: ({ row }) => <PayloadCell payload={row.original.payload} />,
    } satisfies ColumnDef<HighnoteWebhookEventItem>,
  },
] as const

export type SelectableColumns = (typeof selectableColumns)[number]['id']

export const IDColumn: ColumnDef<HighnoteWebhookEventItem> = {
  header: 'ID',
  accessorKey: 'id',
}
